import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, ArrowUpRight, MousePointerClick, X } from 'lucide-react';
import AccountTransactionPanel from './AccountTransactionPanel';
import JournalEntryPopup from './JournalEntryPopup';
import { tr } from '../lib/i18nHelpers';

interface AccountLine {
  account_code: string;
  account_name: string;
  amount: number;
}

interface PnlData {
  revenue: number;
  cost: number;
  gross_profit: number;
  /** Operating expenses only (excludes `other_expenses`). */
  expenses: number;
  operating_profit?: number;
  other_income?: number;
  other_expenses?: number;
  net_income: number;
  revenue_accounts?: AccountLine[];
  cost_accounts?: AccountLine[];
  expense_accounts?: AccountLine[];
  other_income_accounts?: AccountLine[];
  other_expense_accounts?: AccountLine[];
  source?: 'journal' | 'bank';
  include_drafts?: boolean;
  period?: { start: string; end: string };
}

type DetailKey =
  | 'revenue'
  | 'cost'
  | 'gross_profit'
  | 'operating_expenses'
  | 'operating_profit'
  | 'other_income'
  | 'other_expenses'
  | 'net_income';

interface PnlFormulaBannerProps {
  data: PnlData;
  /** Deep link from the dashboard, e.g. `pl_focus=revenue`. */
  focus?: string | null;
  /** Optional deep link for unposted bank transactions inside account details. */
  onOpenBankTransaction?: (bankStatementId: string, transactionId: string) => void;
}

interface Block {
  key: DetailKey;
  label: string;
  value: number;
  accounts: AccountLine[];
  description: string;
  variant?: 'input' | 'subtotal' | 'total';
}


const money = (value: number) => `HKD ${(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

type SemanticTone = 'income' | 'expense' | 'result';

interface Tone {
  card: string;
  label: string;
  amount: string;
  meta: string;
  action: string;
  dot: string;
}

const TONES: Record<SemanticTone, Tone> = {
  income: {
    card: 'border-l-4 border-l-emerald-500 border-border bg-card hover:border-border hover:bg-muted/30',
    label: 'text-muted-foreground',
    amount: 'text-foreground',
    meta: 'text-muted-foreground',
    action: 'text-emerald-900 dark:text-emerald-200',
    dot: 'bg-emerald-500',
  },
  expense: {
    card: 'border-l-4 border-l-red-500 border-border bg-card hover:border-border hover:bg-muted/30',
    label: 'text-muted-foreground',
    amount: 'text-foreground',
    meta: 'text-muted-foreground',
    action: 'text-red-800 dark:text-red-200',
    dot: 'bg-red-500',
  },
  result: {
    card: 'border-primary/25 bg-primary/5 hover:border-primary hover:bg-primary/10',
    label: 'text-primary',
    amount: 'text-foreground',
    meta: 'text-muted-foreground',
    action: 'text-primary',
    dot: 'bg-primary',
  },
};

const SEMANTIC_TONES: Record<DetailKey, SemanticTone> = {
  revenue: 'income',
  cost: 'expense',
  gross_profit: 'result',
  operating_expenses: 'expense',
  operating_profit: 'result',
  other_income: 'income',
  other_expenses: 'expense',
  net_income: 'result',
};

const percentOfRevenue = (value: number, revenue: number) => {
  if (!revenue) return null;
  return `${Math.round((value / revenue) * 1000) / 10}%`;
};

function BlockButton({
  block,
  revenue,
  onPress,
}: {
  block: Block;
  revenue: number;
  onPress: (key: DetailKey) => void;
}) {
  const isTotal = block.variant === 'total';
  const isSubtotal = block.variant === 'subtotal';
  const tone = TONES[SEMANTIC_TONES[block.key]];
  const isNegativeResult = Boolean(block.variant) && block.value < 0;
  const isNegativeInput = !block.variant && block.value < 0;
  const share = percentOfRevenue(block.value, revenue);
  const accountCount = block.accounts.length;

  return (
    <button
      type="button"
      onClick={() => onPress(block.key)}
      aria-haspopup="dialog"
      aria-label={`${block.label}. ${money(block.value)}. ${tr('View details', '查看詳情', '查看详情')}`}
      className={[
        'group relative min-w-[128px] flex-1 rounded-xl border p-3 text-left transition-all',
        'hover:-translate-y-0.5 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        isTotal ? 'shadow-sm ring-1 ring-inset ring-primary/20' : '',
        isNegativeResult
          ? 'border-red-300 bg-red-50/50 dark:border-red-800/60 dark:bg-red-950/20'
          : tone.card,
      ].join(' ')}
    >
      <span className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${isNegativeResult ? 'bg-red-500' : tone.dot}`}
            aria-hidden="true"
          />
          <span className={`text-[11px] font-semibold uppercase tracking-wide ${tone.label}`}>
            {block.label}
          </span>
        </span>
        <ArrowUpRight
          className={`h-3.5 w-3.5 transition-opacity ${
            isNegativeResult || isNegativeInput ? 'text-red-900 dark:text-red-200' : tone.meta
          } group-hover:opacity-100`}
          aria-hidden="true"
        />
      </span>

      <span
        className={`mt-2 block font-mono font-semibold tabular-nums ${
          isTotal ? 'text-xl' : isSubtotal ? 'text-lg' : 'text-base'
        } ${isNegativeResult || isNegativeInput ? 'text-red-900 dark:text-red-200' : tone.amount}`}
      >
        {money(block.value)}
      </span>

      <span className={`mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] ${tone.meta}`}>
        <span>{accountCount} {tr('accounts', '個科目', '个科目')}</span>
        {share && <span aria-hidden="true">•</span>}
        {share && <span>{share} {tr('of revenue', '佔收入', '占收入')}</span>}
      </span>

      <span
        className={`mt-2 inline-flex items-center gap-1 text-[11px] font-medium ${
          isNegativeResult || isNegativeInput ? 'text-red-900 dark:text-red-200' : tone.action
        }`}
      >
        <MousePointerClick className="h-3 w-3" aria-hidden="true" />
        {tr('View details', '查看詳情', '查看详情')}
      </span>
    </button>
  );
}

function Operator({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center px-1 text-xl font-bold text-muted-foreground" aria-hidden="true">
      {label}
    </div>
  );
}

function DetailDialog({
  detail,
  data,
  onClose,
  onOpenBankTransaction,
}: {
  detail: Block;
  data: PnlData;
  onClose: () => void;
  onOpenBankTransaction?: (bankStatementId: string, transactionId: string) => void;
}) {
  const [selectedAccount, setSelectedAccount] = useState<AccountLine | null>(null);
  const [selectedJournalEntry, setSelectedJournalEntry] = useState<any | null>(null);
  const backToDetail = () => setSelectedAccount(null);
  const backToAccount = () => setSelectedJournalEntry(null);
  const openAccount = (account: AccountLine) => {
    setSelectedJournalEntry(null);
    setSelectedAccount(account);
  };

  useEffect(() => {
    setSelectedAccount(null);
    setSelectedJournalEntry(null);
  }, [detail.key]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (selectedJournalEntry) backToAccount();
        else if (selectedAccount) backToDetail();
        else onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [selectedJournalEntry, selectedAccount, onClose]);

  const share = percentOfRevenue(detail.value, data.revenue);
  const sortedAccounts = useMemo(
    () => [...detail.accounts].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)),
    [detail.accounts],
  );
  const sourceLabel = data.source === 'bank'
    ? tr('Bank estimate', '銀行交易估算', '银行交易估算')
    : tr('Posted journal entries', '已過帳分錄', '已过账分录');

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={selectedJournalEntry
        ? backToAccount
        : selectedAccount
          ? backToDetail
          : onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pnl-detail-title"
        className="flex h-[80vh] w-[80vw] max-w-none flex-col overflow-hidden rounded-2xl border bg-card shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b bg-muted/20 p-5">
          <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {selectedJournalEntry
                  ? tr('Journal entry detail', '分錄詳情', '分录详情')
                  : tr('Income statement detail', '損益表明細', '损益表明细')}
              </p>
              <h3 id="pnl-detail-title" className="mt-1 text-xl font-bold text-foreground">
                {selectedJournalEntry
                  ? (selectedJournalEntry.entry_number || selectedJournalEntry.entry_id)
                  : selectedAccount
                  ? `${selectedAccount.account_code} – ${selectedAccount.account_name}`
                  : detail.label}
              </h3>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                {selectedJournalEntry
                  ? selectedJournalEntry.description
                  : selectedAccount
                  ? tr('Account transactions for the selected period.', '所選期間的科目交易。', '所选期间的科目交易。')
                  : detail.description}
            </p>
            {!selectedAccount && data.period && (
              <p className="mt-2 text-xs text-muted-foreground">
                {tr('Period', '期間', '期间')}: {data.period.start} – {data.period.end} · {tr('Source', '來源', '来源')}: {sourceLabel}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {selectedAccount && (
              <button
                type="button"
                onClick={selectedJournalEntry ? backToAccount : backToDetail}
                className="inline-flex items-center gap-1 rounded-md border bg-background px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={selectedJournalEntry
                  ? tr('Back to account transactions', '返回科目交易', '返回科目交易')
                  : `${tr('Back to', '返回', '返回')} ${detail.label}`}
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                {tr('Back', '返回', '返回')}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border bg-background p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={tr('Close', '關閉', '关闭')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {selectedAccount ? (
          selectedJournalEntry ? (
            <JournalEntryPopup
              embedded
              entry={selectedJournalEntry}
              onClose={backToAccount}
            />
          ) : (
            <div className="flex-1 overflow-y-auto">
              <AccountTransactionPanel
              accountCode={selectedAccount.account_code}
              accountName={selectedAccount.account_name}
              startDate={data.period?.start || ''}
              endDate={data.period?.end || ''}
              includeDrafts={data.include_drafts}
              onClose={backToDetail}
              onPostClick={onOpenBankTransaction || (() => {})}
              onSelectEntry={setSelectedJournalEntry}
            />
            </div>
          )
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto p-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border bg-background p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{tr('Amount', '金額', '金额')}</p>
                <p className="mt-1 text-xl font-mono font-semibold text-foreground">{money(detail.value)}</p>
              </div>
              <div className="rounded-xl border bg-background p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{tr('Linked accounts', '關聯科目', '关联科目')}</p>
                <p className="mt-1 text-xl font-semibold text-foreground">{detail.accounts.length}</p>
              </div>
              <div className="rounded-xl border bg-background p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{tr('Share of revenue', '佔收入', '占收入')}</p>
                <p className="mt-1 text-xl font-semibold text-foreground">{share ? `${share}` : '—'}</p>
              </div>
            </div>

          <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h4 className="text-sm font-semibold text-foreground">
                    {tr('Account breakdown', '科目明細', '科目明细')}
                  </h4>
                  {sortedAccounts.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {tr('Press View breakdown for transactions', '按「查看明細」查看交易', '按「查看明细」查看交易')}
                    </span>
                  )}
                </div>

                {sortedAccounts.length === 0 ? (
                  <div className="rounded-xl border border-dashed bg-background p-6 text-center">
                    <p className="text-sm font-medium text-foreground">
                      {tr('No linked accounts', '沒有關聯科目', '没有关联科目')}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {tr('This value may come from uncategorized bank activity or an empty accounting period.', '此數值可能來自未分類銀行活動或空會計期間。', '此数值可能来自未分类银行活动或空会计期间。')}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-xl border bg-background">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/30 text-xs text-muted-foreground">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium">{tr('Code', '編號', '编号')}</th>
                          <th className="px-4 py-2 text-left font-medium">{tr('Account', '科目', '科目')}</th>
                          <th className="px-4 py-2 text-right font-medium">{tr('Amount', '金額', '金额')}</th>
                          <th className="px-4 py-2 text-right font-medium">{tr('Share', '佔比', '占比')}</th>
                          <th className="px-4 py-2 text-right font-medium"><span className="sr-only">{tr('Actions', '操作', '操作')}</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedAccounts.map((account) => {
                          const rowShare = percentOfRevenue(account.amount, data.revenue);
                          return (
                            <tr
                              key={`${account.account_code}-${account.account_name}`}
                              tabIndex={0}
                              role="button"
                              aria-label={`${account.account_code} ${account.account_name}. ${tr('View account transactions', '查看科目交易', '查看科目交易')}`}
                              onClick={() => openAccount(account)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  openAccount(account);
                                }
                              }}
                              className="cursor-pointer border-t border-border/60 transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                            >
                              <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{account.account_code}</td>
                              <td className="px-4 py-3 text-foreground">{account.account_name}</td>
                              <td className={`px-4 py-3 text-right font-mono ${account.amount < 0 ? 'text-red-900 dark:text-red-200' : 'text-foreground'}`}>
                                {money(account.amount)}
                              </td>
                              <td className="px-4 py-3 text-right text-muted-foreground">{rowShare || '—'}</td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openAccount(account);
                                  }}
                                  className="inline-flex items-center gap-1 rounded-md border bg-background px-2.5 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                  aria-label={`${account.account_code} ${account.account_name}. ${tr('View breakdown', '查看明細', '查看明细')}`}
                                >
                                  {tr('View breakdown', '查看明細', '查看明细')}
                                  <ArrowRight className="h-3 w-3" aria-hidden="true" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PnlFormulaBanner({
  data,
  focus,
  onOpenBankTransaction,
}: PnlFormulaBannerProps) {
  const operatingProfit = data.operating_profit ?? ((data.gross_profit || 0) - (data.expenses || 0));
  const otherIncome = data.other_income || 0;
  const otherExpenses = data.other_expenses || 0;

  const blocks: Record<DetailKey, Block> = {
    revenue: {
      key: 'revenue',
      label: tr('Revenue', '收入', '收入'),
      value: data.revenue || 0,
      accounts: data.revenue_accounts || [],
      description: tr(
        'Operating revenue earned in the selected period before direct costs and operating expenses.',
        '選定期間內已賺取的營業收入，未扣除直接成本及營運支出。',
        '选定期间内已赚取的营业收入，未扣除直接成本及营运支出。',
      ),
    },
    cost: {
      key: 'cost',
      label: tr('Direct Costs', '直接成本', '直接成本'),
      value: data.cost || 0,
      accounts: data.cost_accounts || [],
      description: tr(
        'Costs directly attributable to delivering the goods or services that generated revenue.',
        '與提供產生收入的商品或服務直接相關的成本。',
        '与提供产生收入的商品或服务直接相关的成本。',
      ),
    },
    gross_profit: {
      key: 'gross_profit',
      label: tr('Gross Profit', '毛利', '毛利'),
      value: data.gross_profit || 0,
      accounts: [...(data.revenue_accounts || []), ...(data.cost_accounts || [])],
      description: tr(
        'Trading result after deducting direct costs from operating revenue.',
        '營業收入扣除直接成本後的貿易成果。',
        '营业收入扣除直接成本后的贸易成果。',
      ),
      variant: 'subtotal',
    },
    operating_expenses: {
      key: 'operating_expenses',
      label: tr('Operating Expenses', '營運支出', '营运支出'),
      value: data.expenses || 0,
      accounts: data.expense_accounts || [],
      description: tr(
        'Running costs excluded from direct costs, such as salaries, rent, admin, and depreciation.',
        '未計入直接成本的營運開支，例如薪金、租金、行政費及折舊。',
        '未计入直接成本的营运开支，例如薪金、租金、行政费及折旧。',
      ),
    },
    operating_profit: {
      key: 'operating_profit',
      label: tr('Operating Profit', '營業利潤', '营业利润'),
      value: operatingProfit || 0,
      accounts: [...(data.revenue_accounts || []), ...(data.cost_accounts || []), ...(data.expense_accounts || [])],
      description: tr(
        'Profit from core operations before other income and other expenses.',
        '扣除其他收入及其他支出前的核心業務利潤。',
        '扣除其他收入及其他支出前的核心业务利润。',
      ),
      variant: 'subtotal',
    },
    other_income: {
      key: 'other_income',
      label: tr('Other Income', '其他收入', '其他收入'),
      value: otherIncome,
      accounts: data.other_income_accounts || [],
      description: tr(
        'Income outside normal trading operations, such as interest or one-off recoveries.',
        '正常業務以外的收入，例如利息或一次性回收。',
        '正常业务以外的收入，例如利息或一次性回收。',
      ),
    },
    other_expenses: {
      key: 'other_expenses',
      label: tr('Other Expenses', '其他支出', '其他支出'),
      value: otherExpenses,
      accounts: data.other_expense_accounts || [],
      description: tr(
        'Non-operating costs outside the core trading result.',
        '核心業務成果以外的非營運支出。',
        '核心业务成果以外的非营运支出。',
      ),
    },
    net_income: {
      key: 'net_income',
      label: tr('Net Profit', '淨利', '淨利'),
      value: data.net_income || 0,
      accounts: [
        ...(data.revenue_accounts || []),
        ...(data.cost_accounts || []),
        ...(data.expense_accounts || []),
        ...(data.other_income_accounts || []),
        ...(data.other_expense_accounts || []),
      ],
      description: tr(
        'Final result after direct costs, operating expenses, other income, and other expenses.',
        '扣除直接成本、營運支出、其他收入及其他支出後的最終成果。',
        '扣除直接成本、营运支出、其他收入及其他支出后的最终成果。',
      ),
      variant: 'total',
    },
  };

  const [activeDetail, setActiveDetail] = useState<DetailKey | null>(null);

  useEffect(() => {
    if (focus === 'revenue') setActiveDetail('revenue');
    else if (focus === 'expense') setActiveDetail('operating_expenses');
    else if (focus === 'net') setActiveDetail('net_income');
  }, [focus]);

  const openDetail = (key: DetailKey) => setActiveDetail(key);
  const activeBlock = activeDetail ? blocks[activeDetail] : null;

  return (
    <section
      aria-labelledby="pnl-formula-heading"
      className="rounded-2xl border bg-card p-5 shadow-sm"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 id="pnl-formula-heading" className="text-base font-semibold text-foreground">
            {tr('Income statement formula', '損益表公式', '损益表公式')}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {tr(
              'Select any block to open its account detail.',
              '選擇任何區塊以開啟科目明細。',
              '选择任何区块以开启科目明细。',
            )}
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {tr('Trading result', '貿易成果', '贸易成果')}
          </p>
          <div className="flex flex-col items-stretch gap-2 lg:flex-row lg:items-center">
            <BlockButton block={blocks.revenue} revenue={data.revenue || 0} onPress={openDetail} />
            <Operator label="−" />
            <BlockButton block={blocks.cost} revenue={data.revenue || 0} onPress={openDetail} />
            <Operator label="=" />
            <BlockButton block={blocks.gross_profit} revenue={data.revenue || 0} onPress={openDetail} />
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {tr('Operating result', '營業成果', '营业成果')}
          </p>
          <div className="flex flex-col items-stretch gap-2 lg:flex-row lg:items-center">
            <BlockButton block={blocks.gross_profit} revenue={data.revenue || 0} onPress={openDetail} />
            <Operator label="−" />
            <BlockButton block={blocks.operating_expenses} revenue={data.revenue || 0} onPress={openDetail} />
            <Operator label="=" />
            <BlockButton block={blocks.operating_profit} revenue={data.revenue || 0} onPress={openDetail} />
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {tr('Final result', '最終成果', '最终成果')}
          </p>
          <div className="flex flex-col items-stretch gap-2 lg:flex-row lg:items-center">
            <BlockButton block={blocks.operating_profit} revenue={data.revenue || 0} onPress={openDetail} />
            <Operator label="+" />
            <BlockButton block={blocks.other_income} revenue={data.revenue || 0} onPress={openDetail} />
            <Operator label="−" />
            <BlockButton block={blocks.other_expenses} revenue={data.revenue || 0} onPress={openDetail} />
            <Operator label="=" />
            <BlockButton block={blocks.net_income} revenue={data.revenue || 0} onPress={openDetail} />
          </div>
        </div>
      </div>

      {activeBlock && (
        <DetailDialog
          key={activeDetail}
          detail={activeBlock}
          data={data}
          onClose={() => setActiveDetail(null)}
          onOpenBankTransaction={onOpenBankTransaction}
        />
      )}
    </section>
  );
}
